import { ModelAiSuggestion, SuggestHyperparamsRequest } from "@repo/schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DatasetSplit } from "../components/DatasetSplitSettings";
import {
  useDatasetStatsMutation,
  useSuggestHyperparamsMutation,
} from "../services";
import { useApiErrorMessage } from "@/core/api";

export type AutoSuggestionStatus =
  | "disabled"
  | "checking"
  | "loading"
  | "applied"
  | "skipped"
  | "failed";

export interface AppliedAutoSuggestion {
  epochs: number;
  split: DatasetSplit;
  params: Record<string, string | number | boolean>;
  labelIds: number[];
}

export interface UseAutoSuggestionArgs {
  /** False disables the whole flow (e.g. when duplicating a model) */
  enabled: boolean;
  projectId: number | undefined;
  /** Fresh form-state snapshot; re-read on every run so Retry uses live values */
  getContext: () => SuggestHyperparamsRequest & { projectId: number };
  onApply: (result: AppliedAutoSuggestion) => void;
}

export interface UseAutoSuggestionResult {
  status: AutoSuggestionStatus;
  errorMessage: string | null;
  /** Persistable snapshot of the last successful run; null otherwise */
  aiSuggestion: ModelAiSuggestion | null;
  /** Abort the in-flight request and fall through to the plain form */
  skip: () => void;
  /** Re-run the suggestion after a failure */
  retry: () => void;
}

const getErrorStatus = (err: unknown): number | null =>
  typeof err === "object" &&
  err !== null &&
  "status" in err &&
  typeof (err as { status?: unknown }).status === "number"
    ? (err as { status: number }).status
    : null;

/**
 * Drives the automatic suggest-with-AI flow on the new-version form:
 *
 *   disabled ─ (enabled=false)
 *   checking ─ dataset invalid / pre-check error ──────────▶ skipped
 *      │ valid
 *   loading ── user skip / Gemini disabled (503) ──────────▶ skipped
 *      │           └─ other error ──▶ failed ── retry() ──▶ loading
 *      ▼ success (onApply fired)
 *   applied
 *
 * The run starts once per mount (StrictMode's aborted first pass is
 * superseded via a run-id guard); aborting on unmount keeps a navigation
 * away from leaking the request.
 */
export const useAutoSuggestion = ({
  enabled,
  projectId,
  getContext,
  onApply,
}: UseAutoSuggestionArgs): UseAutoSuggestionResult => {
  const { t } = useTranslation(["model", "common"]);
  const apiErrorMessage = useApiErrorMessage();
  const [checkDataset] = useDatasetStatsMutation();
  const [suggest] = useSuggestHyperparamsMutation();

  const [status, setStatus] = useState<AutoSuggestionStatus>(() =>
    enabled ? "checking" : "disabled",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<ModelAiSuggestion | null>(
    null,
  );

  // Latest-run guard: async completions from a superseded run (skipped,
  // unmounted, or StrictMode's first pass) compare against the current id
  // and bail out instead of touching state.
  const runIdRef = useRef(0);
  const requestRef = useRef<{ abort: () => void } | null>(null);

  const getContextRef = useRef(getContext);
  getContextRef.current = getContext;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  const runSuggestion = useCallback(async () => {
    const runId = ++runIdRef.current;
    setStatus("loading");
    setErrorMessage(null);
    const request = suggest(getContextRef.current());
    requestRef.current = request;
    try {
      const data = await request.unwrap();
      if (runId !== runIdRef.current) return;
      const params = Object.fromEntries(
        Object.entries(data.params).map(([name, param]) => [
          name,
          param.value,
        ]),
      );
      onApplyRef.current({
        epochs: data.epochs.value,
        split: {
          train: data.split.train,
          validation: data.split.validation,
          test: data.split.test,
        },
        params,
        labelIds: data.labels.labelIds,
      });
      setAiSuggestion({ warnings: data.warnings, summary: data.summary });
      setStatus("applied");
      toast.success(t("autoSuggestion.applied"));
    } catch (err) {
      if (runId !== runIdRef.current) return;
      // Gemini not configured on this deployment — silently fall through.
      if (getErrorStatus(err) === 503) {
        setStatus("skipped");
        return;
      }
      setErrorMessage(apiErrorMessage(err, t("autoSuggestion.failed")));
      setStatus("failed");
    }
  }, [suggest, t, apiErrorMessage]);

  useEffect(() => {
    if (!enabled || !projectId) return;
    const runId = ++runIdRef.current;
    setStatus("checking");
    const context = getContextRef.current();
    const request = checkDataset({
      projectId,
      taskIds: context.taskIds,
      trainingType: context.trainingType,
      annotationsUsed: context.annotationsUsed,
    });
    requestRef.current = request;
    request
      .unwrap()
      .then((stats) => {
        if (runId !== runIdRef.current) return;
        if (!stats.valid) {
          // The form's own dataset validation will surface the error.
          setStatus("skipped");
          return;
        }
        void runSuggestion();
      })
      .catch(() => {
        if (runId !== runIdRef.current) return;
        setStatus("skipped");
      });
    return () => {
      runIdRef.current++;
      requestRef.current?.abort();
    };
    // checkDataset/runSuggestion are stable mutation-backed callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, projectId]);

  const skip = useCallback(() => {
    runIdRef.current++;
    requestRef.current?.abort();
    setStatus("skipped");
  }, []);

  const retry = useCallback(() => {
    void runSuggestion();
  }, [runSuggestion]);

  return { status, errorMessage, aiSuggestion, skip, retry };
};
